"use client";
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

export interface Attachment {
  name: string;
  size: number;
  lastModified: number;
  type: string;
  file: File;
}

interface FileContextType {
  uploadedFiles: Attachment[];
  setUploadedFiles: (files: Attachment[]) => void;
  addFiles: (files: Attachment[]) => void;
  clearFiles: () => void;
}

const FileContext = createContext<FileContextType | undefined>(undefined);

export function FileProvider({ children }: { children: ReactNode }) {
  const [uploadedFiles, setUploadedFiles] = useState<Attachment[]>([]);

  const addFiles = useCallback((files: Attachment[]) => {
    setUploadedFiles((prev) => {
      const combined = [...prev, ...files];
      const unique = new Map<string, Attachment>();
      combined.forEach((att) => {
        const key = `${att.name}-${att.size}-${att.lastModified}`;
        if (!unique.has(key)) unique.set(key, att);
      });
      return Array.from(unique.values());
    });
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  const value = useMemo(
    () => ({
      uploadedFiles,
      setUploadedFiles,
      addFiles,
      clearFiles,
    }),
    [uploadedFiles, addFiles, clearFiles],
  );

  return <FileContext.Provider value={value}>{children}</FileContext.Provider>;
}

export function useUploadedFiles() {
  const context = useContext(FileContext);
  if (context === undefined) {
    throw new Error("useUploadedFiles must be used within a FileProvider");
  }
  return context;
}
